import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BlogPost } from '../../types/blog';
import { fetchBlogPosts } from '../../utils/blogUtils';
import LoadingAnimation from '../LoadingAnimation';
import { formatPublishDate } from '../../utils/blogUtils';
import AdminRouteGuard from '../AdminRouteGuard';
import { ADMIN_SECTIONS } from '../../utils/adminPermissions';

interface BlogAdminProps {
  darkMode?: boolean;
}

const BlogAdmin: React.FC<BlogAdminProps> = ({ darkMode = false }) => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [showBlogOnDashboard, setShowBlogOnDashboard] = useState(true);
  const [blogAccessEnabled, setBlogAccessEnabled] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'posts' | 'analytics'>('posts');
  const [analyticsData, setAnalyticsData] = useState<any>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [currentToken, setCurrentToken] = useState<string | null>(null);

  // Helper function to get the current user's token
  const getCurrentToken = () => {
    return localStorage.getItem('token') || localStorage.getItem('recruiterToken');
  };

  useEffect(() => {
    checkUserAccess();
  }, []);

  useEffect(() => {
    if (!accessDenied && userRole) {
      loadBlogPosts();
      loadSettings();
    }
  }, [accessDenied, userRole]);

  useEffect(() => {
    if (activeTab === 'analytics') {
      loadAnalytics();
    }
  }, [activeTab]);

  // Add refresh functionality
  const refreshPosts = () => {
    loadBlogPosts();
  };

  // Count posts pending review (recruiter drafts)
  const getPendingReviewCount = () => {
    if (userRole !== 'admin') return 0;
    
    return posts.filter(post => 
      !post.sys.publishedAt && 
      post.fields?.author?.fields?.name && 
      post.fields.author.fields.name !== 'Admin'
    ).length;
  };

  const checkUserAccess = async () => {
    try {
      setCheckingAccess(true);
      
      // Check for admin token (from admin login)
      const adminToken = localStorage.getItem('token');
      // Check for recruiter token (from recruiter login)
      const recruiterToken = localStorage.getItem('recruiterToken');
      
      let token = null;
      let role = null;
      
      if (adminToken) {
        token = adminToken;
        role = 'admin';
        setCurrentToken(adminToken);
      } else if (recruiterToken) {
        token = recruiterToken;
        role = 'recruiter';
        setCurrentToken(recruiterToken);
      }
      
      console.log('Checking blog access. Admin token:', !!adminToken, 'Recruiter token:', !!recruiterToken, 'Role:', role);
      
      if (!token || (role !== 'recruiter' && role !== 'admin')) {
        console.log('User not authenticated or wrong role');
        setAccessDenied(true);
        setCheckingAccess(false);
        return;
      }
      
      // If user is admin, always allow access
      if (role === 'admin') {
        setUserRole(role);
        setAccessDenied(false);
        setCheckingAccess(false);
        return;
      }
      
      // If user is recruiter, check if blog access is enabled
      if (role === 'recruiter') {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        const response = await fetch(`${apiUrl}/api/admin/settings/blog_access_enabled`);
        
        if (response.ok) {
          const data = await response.json();
          const blogAccessEnabled = data.setting_value === 'true';
          
          if (blogAccessEnabled) {
            setUserRole(role);
            setAccessDenied(false);
          } else {
            console.log('Blog access disabled for recruiters');
            setAccessDenied(true);
          }
        } else {
          console.log('Failed to check blog access setting, denying access');
          setAccessDenied(true);
        }
      }
    } catch (error) {
      console.error('Error checking user access:', error);
      setAccessDenied(true);
    } finally {
      setCheckingAccess(false);
    }
  };

  const loadBlogPosts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use admin endpoint to get drafts and published posts
      const token = getCurrentToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      
      if (token) {
        // Try to fetch from admin endpoint first (includes drafts)
        try {
          const adminResponse = await fetch(`${apiUrl}/api/blog/admin/posts`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (adminResponse.ok) {
            const adminData = await adminResponse.json();
            setPosts(adminData.items || []);
            return;
          }
        } catch (adminErr) {
          // Silently fall back to public API
        }
      }
      
      // Fallback to public API (published posts only)
      const response = await fetchBlogPosts({ limit: 100 });
      setPosts(response.items);
    } catch (err) {
      console.error('Error loading blog posts:', err);
      setError('Failed to load blog posts');
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    navigate('/blog-admin/new');
  };

  const handleEditPost = (postId: string) => {
    navigate(`/blog-admin/edit/${postId}`);
  };

  const handleDeletePost = async (postId: string) => {
    if (!window.confirm('Are you sure you want to delete this post?')) {
      return;
    }

    try {
      const token = getCurrentToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      
      console.log(`DEBUG: Attempting to delete post ${postId}`);
      
      const response = await fetch(`${apiUrl}/api/blog/${postId}`, {
        method: 'DELETE',
        headers: token ? {
          'Authorization': `Bearer ${token}`
        } : {}
      });

      if (!response.ok) {
        throw new Error('Failed to delete post');
      }

      // Reload posts after deletion
      await loadBlogPosts();
    } catch (err) {
      console.error('Error deleting post:', err);
      alert('Failed to delete post');
    }
  };

  const handlePublishPost = async (postId: string) => {
    if (!window.confirm('Are you sure you want to publish this post?')) {
      return;
    }

    try {
      const token = getCurrentToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      
      console.log(`DEBUG: Attempting to publish post ${postId}`);
      
      const response = await fetch(`${apiUrl}/api/blog/${postId}/publish`, {
        method: 'POST',
        headers: token ? {
          'Authorization': `Bearer ${token}`
        } : {}
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to publish post');
      }

      const result = await response.json();
      console.log('Publish response:', result);
      
      if (result.success) {
        alert('Post published successfully!');
        // Reload posts to reflect published status
        await loadBlogPosts();
      } else {
        console.error('Publish failed with result:', result);
        let errorMessage = result.message || result.error || 'Failed to publish post';
        
        // Check for specific Contentful validation errors
        if (result.error && result.error.includes('featuredImage') && result.error.includes('required')) {
          errorMessage = 'Cannot publish: Featured image is required. Please edit the post and add a featured image.';
        }
        
        throw new Error(errorMessage);
      }
    } catch (err) {
      console.error('Error publishing post:', err);
      alert(`Failed to publish post: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const loadSettings = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      
      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);
      
      // Load both settings in parallel
      const [dashboardResponse, blogAccessResponse] = await Promise.all([
        fetch(`${apiUrl}/api/admin/settings/show_blog_on_student_dashboard`, {
          signal: controller.signal
        }),
        fetch(`${apiUrl}/api/admin/settings/blog_access_enabled`, {
          signal: controller.signal
        })
      ]);
      
      clearTimeout(timeoutId);
      
      if (dashboardResponse.ok) {
        const data = await dashboardResponse.json();
        setShowBlogOnDashboard(data.setting_value === 'true');
      }
      
      if (blogAccessResponse.ok) {
        const data = await blogAccessResponse.json();
        setBlogAccessEnabled(data.setting_value === 'true');
      }
    } catch (error) {
      console.log('Admin settings load failed, using defaults:', error);
      // Graceful degradation - keep default values
    }
  };

  const handleToggleBlogDashboard = async () => {
    if (userRole !== 'admin') {
      alert('Only administrators can change this setting');
      return;
    }

    setSettingsLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const token = getCurrentToken();
      const newValue = !showBlogOnDashboard;
      
      const response = await fetch(`${apiUrl}/api/admin/settings/show_blog_on_student_dashboard`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          setting_value: newValue.toString()
        })
      });

      if (response.ok) {
        setShowBlogOnDashboard(newValue);
        console.log('Blog dashboard setting updated successfully');
      } else {
        throw new Error('Failed to update setting');
      }
    } catch (error) {
      console.error('Error updating setting:', error);
      alert('Failed to update setting. Please try again.');
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleToggleBlogAccess = async () => {
    if (userRole !== 'admin') {
      alert('Only administrators can change this setting');
      return;
    }

    setSettingsLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const token = getCurrentToken();
      const newValue = !blogAccessEnabled;
      
      const response = await fetch(`${apiUrl}/api/admin/settings/blog_access_enabled`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          setting_value: newValue.toString()
        })
      });

      if (response.ok) {
        setBlogAccessEnabled(newValue);
        console.log('Blog access setting updated successfully');
      } else {
        throw new Error('Failed to update setting');
      }
    } catch (error) {
      console.error('Error updating setting:', error);
      alert('Failed to update setting. Please try again.');
    } finally {
      setSettingsLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      
      console.log('🔍 Loading analytics from:', `${apiUrl}/api/blog/analytics/summary`);
      
      const response = await fetch(`${apiUrl}/api/blog/analytics/summary`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Analytics data loaded:', data);
        console.log('📊 Detailed analytics:', {
          totalViews: data.data?.total_views,
          uniqueSessions: data.data?.unique_sessions,
          popularPosts: data.data?.popular_posts,
          recentViews: data.data?.recent_views_7d
        });
        setAnalyticsData(data);
      } else {
        console.warn('⚠️ Failed to load analytics data. Status:', response.status, response.statusText);
        setAnalyticsData(null);
      }
    } catch (error) {
      console.warn('❌ Error loading analytics:', error);
      setAnalyticsData(null);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  if (checkingAccess || loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingAnimation onComplete={() => {}} />
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
            <p className="text-gray-600 mb-6">
              {userRole === 'recruiter' 
                ? 'Blog management is currently disabled for recruiters. Please contact an administrator.'
                : 'You do not have permission to access the blog administration panel.'}
            </p>
            <button
              onClick={() => window.history.back()}
              className="px-6 py-2 bg-[#00A0DF] text-white rounded-lg hover:bg-[#00A0DF]/90 transition-colors"
            >
              ← Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AdminRouteGuard requiredPermission={ADMIN_SECTIONS.BLOG_MANAGEMENT}>
      <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          {/* Pending Review Notification for Admins */}
          {userRole === 'admin' && getPendingReviewCount() > 0 && (
            <div className="mb-4 p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-orange-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p className="text-orange-800 dark:text-orange-200 font-medium">
                  {getPendingReviewCount()} post{getPendingReviewCount() > 1 ? 's' : ''} pending review
                </p>
              </div>
            </div>
          )}
          <h1 className="text-3xl font-bold mb-4">Blog Administration</h1>
          <div className="flex justify-between items-center mb-6">
            <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {userRole === 'admin' ? 'Manage blog posts and settings' : 'Create and submit blog posts for review'}. You are logged in as: <strong>{userRole}</strong>
            </p>
            {activeTab === 'posts' && (
              <div className="flex space-x-3">
                <button
                  onClick={refreshPosts}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    darkMode
                      ? 'bg-gray-700 text-white hover:bg-gray-600'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : '↻ Refresh'}
                </button>
                <button
                  onClick={handleCreateNew}
                  className="px-6 py-2 bg-[#00A0DF] text-white rounded-lg hover:bg-[#00A0DF]/90 transition-colors"
                >
                  + Create New Post
                </button>
              </div>
            )}
            {activeTab === 'analytics' && (
              <div className="flex space-x-3">
                <button
                  onClick={loadAnalytics}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    darkMode
                      ? 'bg-gray-700 text-white hover:bg-gray-600'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                  disabled={analyticsLoading}
                >
                  {analyticsLoading ? 'Loading...' : '↻ Refresh Analytics'}
                </button>
                <div className={`px-3 py-2 rounded text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Last updated: {analyticsData ? new Date().toLocaleTimeString() : 'Never'}
                </div>
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('posts')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'posts'
                    ? 'border-[#00A0DF] text-[#00A0DF]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Blog Posts
              </button>
              <button
                onClick={() => setActiveTab('analytics')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'analytics'
                    ? 'border-[#00A0DF] text-[#00A0DF]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Analytics
              </button>
              
              {/* Navigation Buttons */}
              <div className="ml-auto flex space-x-3">
                <button
                  onClick={() => navigate('/blog')}
                  className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                    darkMode
                      ? 'bg-gray-700 text-white hover:bg-gray-600'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  ← View Public Blog
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="px-4 py-2 bg-[#00A0DF] text-white rounded-lg text-sm hover:bg-[#00A0DF]/90 transition-colors"
                >
                  Back to NACME Searchlight
                </button>
              </div>
            </nav>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            <p>{error}</p>
          </div>
        )}

        {/* Posts Tab Content */}
        {activeTab === 'posts' && (
          <>
            {/* Settings Section - Only show for admins */}
            {userRole === 'admin' && (
              <div className={`mb-6 rounded-lg shadow p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                <h2 className="text-lg font-semibold mb-4">Blog Settings</h2>
                <div className="space-y-6">
                  {/* Student Dashboard Setting */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium">Show Blog Posts on Student Dashboard</h3>
                      <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        When enabled, students will see recent blog posts on their dashboard
                      </p>
                    </div>
                    <button
                      onClick={handleToggleBlogDashboard}
                      disabled={settingsLoading}
                      className={`
                        relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                        ${showBlogOnDashboard ? 'bg-blue-600' : 'bg-gray-200'}
                        ${settingsLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                      `}
                    >
                      <span
                        className={`
                          inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                          ${showBlogOnDashboard ? 'translate-x-6' : 'translate-x-1'}
                        `}
                      />
                    </button>
                  </div>

                  {/* Recruiter Blog Access Setting */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium">Enable Recruiter Blog Access</h3>
                      <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        When enabled, recruiters can create and manage blog posts from their dashboard
                      </p>
                    </div>
                    <button
                      onClick={handleToggleBlogAccess}
                      disabled={settingsLoading}
                      className={`
                        relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                        ${blogAccessEnabled ? 'bg-blue-600' : 'bg-gray-200'}
                        ${settingsLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                      `}
                    >
                      <span
                        className={`
                          inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                          ${blogAccessEnabled ? 'translate-x-6' : 'translate-x-1'}
                        `}
                      />
                    </button>
                  </div>
                </div>
              </div>
            )}

        {/* Posts Table */}
        <div className={`rounded-lg shadow overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200" style={{ minWidth: '800px' }}>
            <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                  Author
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                  Published Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider min-w-[140px]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
              {posts.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center">
                    No blog posts found. Create your first post!
                  </td>
                </tr>
              ) : (
                posts.map((post) => {
                  const isPendingReview = !post.sys.publishedAt && 
                    post.fields?.author?.fields?.name && 
                    post.fields.author.fields.name !== 'Admin';
                  
                  return (
                  <tr key={post.sys.id} className={isPendingReview && userRole === 'admin' ? 'bg-orange-50 dark:bg-orange-900/10' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium">
                          {post.fields?.title || 'Untitled'}
                        </div>
                        <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          /{post.fields?.slug || 'no-slug'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {post.fields?.author?.fields?.name || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {post.fields?.publishedDate ? formatPublishDate(post.fields.publishedDate) : 'Not set'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          post.sys.publishedAt
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {post.sys.publishedAt ? 'Published' : 'Draft'}
                        </span>
                        {/* Show pending review label for recruiter drafts */}
                        {!post.sys.publishedAt && post.fields?.author?.fields?.name && 
                         post.fields.author.fields.name !== 'Admin' && 
                         userRole === 'admin' && (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-orange-100 text-orange-800">
                            Pending Review
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 pr-8 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-4">
                        <button
                          onClick={() => handleEditPost(post.sys.id)}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          Edit
                        </button>
                        {!post.sys.publishedAt && userRole === 'admin' && (
                          <button
                            onClick={() => handlePublishPost(post.sys.id)}
                            className="text-green-600 hover:text-green-900"
                          >
                            {post.fields?.author?.fields?.name && post.fields.author.fields.name !== 'Admin' 
                              ? 'Approve & Publish' 
                              : 'Publish'}
                          </button>
                        )}
                        <button
                          onClick={() => handleDeletePost(post.sys.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
            </table>
          </div>
        </div>
          </>
        )}

        {/* Analytics Tab Content */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            {analyticsLoading ? (
              <div className="flex justify-center items-center min-h-[400px]">
                <LoadingAnimation onComplete={() => {}} />
              </div>
            ) : analyticsData ? (
              <>
                {/* Analytics Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className={`rounded-lg shadow p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                    <h3 className="text-lg font-semibold mb-2">Total Page Views</h3>
                    <p className="text-3xl font-bold text-blue-600">{analyticsData.data?.total_views || 0}</p>
                    <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      All-time page views across all blog posts
                    </p>
                  </div>

                  <div className={`rounded-lg shadow p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                    <h3 className="text-lg font-semibold mb-2">Unique Visitors</h3>
                    <p className="text-3xl font-bold text-green-600">{analyticsData.data?.unique_sessions || 0}</p>
                    <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Unique sessions (based on session IDs)
                    </p>
                  </div>

                  <div className={`rounded-lg shadow p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                    <h3 className="text-lg font-semibold mb-2">Popular Posts</h3>
                    <p className="text-3xl font-bold text-purple-600">{analyticsData.data?.popular_posts?.length || 0}</p>
                    <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Posts with recorded views
                    </p>
                  </div>
                </div>

                {/* Popular Posts Table */}
                {analyticsData.data?.popular_posts && analyticsData.data.popular_posts.length > 0 && (
                  <div className={`rounded-lg shadow overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h3 className="text-lg font-semibold">Most Popular Posts</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                              Post
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                              Views
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                          {analyticsData.data.popular_posts.map((post: any, index: number) => (
                            <tr key={post.blog_post_id}>
                              <td className="px-6 py-4">
                                <div>
                                  <div className="text-sm font-medium">
                                    {posts.find(p => p.sys.id === post.blog_post_id)?.fields?.title || 'Unknown Post'}
                                  </div>
                                  <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    ID: {post.blog_post_id}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="text-sm font-semibold">{post.view_count}</span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <button
                                  onClick={() => handleEditPost(post.blog_post_id)}
                                  className="text-indigo-600 hover:text-indigo-900 mr-4"
                                >
                                  Edit Post
                                </button>
                                <button
                                  onClick={() => {
                                    const foundPost = posts.find(p => p.sys.id === post.blog_post_id);
                                    if (foundPost?.fields?.slug) {
                                      window.open(`/blog/${foundPost.fields.slug}`, '_blank');
                                    }
                                  }}
                                  className="text-blue-600 hover:text-blue-900"
                                >
                                  View Post
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Daily Views Chart (if available) */}
                {analyticsData.data?.daily_views && analyticsData.data.daily_views.length > 0 && (
                  <div className={`rounded-lg shadow p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                    <h3 className="text-lg font-semibold mb-4">Daily Page Views (Last 7 Days)</h3>
                    <div className="space-y-2">
                      {analyticsData.data.daily_views.map((day: any) => (
                        <div key={day.date} className="flex justify-between items-center">
                          <span className="text-sm">{day.date}</span>
                          <div className="flex items-center space-x-2">
                            <div 
                              className="bg-blue-500 h-4 rounded"
                              style={{ 
                                width: `${Math.max(day.views / Math.max(...analyticsData.data.daily_views.map((d: any) => d.views)) * 200, 10)}px` 
                              }}
                            ></div>
                            <span className="text-sm font-medium w-8 text-right">{day.views}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className={`rounded-lg shadow p-8 text-center ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                <h3 className="text-lg font-semibold mb-2">No Analytics Data Available</h3>
                <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Analytics data will appear here once users start viewing blog posts.
                </p>
                <button
                  onClick={loadAnalytics}
                  className="mt-4 px-4 py-2 bg-[#00A0DF] text-white rounded-lg hover:bg-[#00A0DF]/90 transition-colors"
                >
                  Refresh Analytics
                </button>
              </div>
            )}
          </div>
        )}

      </div>
      </div>
    </AdminRouteGuard>
  );
};

export default BlogAdmin;